import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

/**
 * Date Range Selector Component
 * 
 * Provides preset date ranges and custom date selection for analytics filtering
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current date range value
 * @param {Function} props.onChange - Callback when date range changes
 * @param {dayjs.Dayjs} props.customStartDate - Start date for custom range
 * @param {dayjs.Dayjs} props.customEndDate - End date for custom range
 * @param {Function} props.onCustomStartDateChange - Callback for custom start date
 * @param {Function} props.onCustomEndDateChange - Callback for custom end date
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {string} props.size - Size of the component ('small', 'medium')
 */
const DateRangeSelector = ({
  value = '30d',
  onChange = () => {},
  customStartDate = dayjs().subtract(30, 'day'),
  customEndDate = dayjs(),
  onCustomStartDateChange = () => {},
  onCustomEndDateChange = () => {},
  disabled = false,
  size = 'small'
}) => {
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(customStartDate);
  const [tempEndDate, setTempEndDate] = useState(customEndDate);

  // Preset date range options
  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'ytd', label: 'Year to date' },
    { value: 'all', label: 'All time' },
    { value: 'custom', label: 'Custom range...' }
  ];

  /**
   * Handle date range selection
   */
  const handleDateRangeChange = (newValue) => {
    if (newValue === 'custom') {
      setCustomDialogOpen(true);
    } else {
      onChange(newValue);
    }
  };

  /**
   * Handle custom date range confirmation
   */
  const handleCustomRangeConfirm = () => {
    // Validate date range
    if (tempStartDate && tempEndDate && tempStartDate.isValid() && tempEndDate.isValid()) {
      if (tempStartDate.isAfter(tempEndDate)) {
        alert('Start date must be before end date');
        return;
      }

      // Update custom dates
      onCustomStartDateChange(tempStartDate);
      onCustomEndDateChange(tempEndDate);
      onChange('custom');
      setCustomDialogOpen(false);
    } else {
      alert('Please select valid start and end dates');
    }
  };

  /**
   * Handle custom date range cancellation
   */
  const handleCustomRangeCancel = () => {
    // Reset temporary dates
    setTempStartDate(customStartDate);
    setTempEndDate(customEndDate);
    setCustomDialogOpen(false);
  };

  /**
   * Get display label for current selection
   */
  const getDisplayLabel = () => {
    if (value === 'custom') {
      return `${customStartDate.format('MMM D')} - ${customEndDate.format('MMM D, YYYY')}`;
    }
    const option = dateRangeOptions.find(opt => opt.value === value);
    return option ? option.label : 'Select range';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size={size} sx={{ minWidth: 160 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={value}
            onChange={(e) => handleDateRangeChange(e.target.value)}
            label="Date Range"
            disabled={disabled}
            startAdornment={<DateRangeIcon sx={{ mr: 1, color: 'action.active' }} />}
            renderValue={() => getDisplayLabel()}
          >
            {dateRangeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Range Dialog */}
        <Dialog 
          open={customDialogOpen} 
          onClose={handleCustomRangeCancel}
          maxWidth="sm"
          fullWidth
          aria-labelledby="custom-date-range-title"
        >
          <DialogTitle id="custom-date-range-title">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateRangeIcon />
              Select Custom Date Range
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={tempStartDate}
                  onChange={(newValue) => setTempStartDate(newValue)}
                  maxDate={tempEndDate || dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: tempStartDate && tempEndDate && tempStartDate.isAfter(tempEndDate)
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={tempEndDate}
                  onChange={(newValue) => setTempEndDate(newValue)}
                  minDate={tempStartDate}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: tempStartDate && tempEndDate && tempStartDate.isAfter(tempEndDate)
                    }
                  }}
                />
              </Grid>
            </Grid>
            {tempStartDate && tempEndDate && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Selected Range: {tempStartDate.format('MMM D, YYYY')} - {tempEndDate.format('MMM D, YYYY')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: {tempEndDate.diff(tempStartDate, 'day') + 1} days
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCustomRangeCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleCustomRangeConfirm} 
              variant="contained"
              disabled={!tempStartDate || !tempEndDate || tempStartDate.isAfter(tempEndDate)}
            >
              Apply Range
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;